/**
 * Tender Links Table Component
 * Displays paginated list of tender link operations
 */

import type { TenderLinkSummary, TenderLinksResponse } from '@tender-monitor/shared/types/admin';
import { formatTimestamp } from '../../utils/formatTime';
import { Pagination } from './Pagination';

type TenderLinksTableProps = {
  data: TenderLinksResponse;
  loading: boolean;
  onPageChange: (page: number) => void;
  onRowClick: (item: TenderLinkSummary) => void;
};

function StatusBadge({ status }: { status: string }) {
  return <span className={`status-badge status-badge--${status}`}>{status}</span>;
}

function ResultCell({ item }: { item: TenderLinkSummary }) {
  if (item.status === 'completed' && item.tenderId) {
    return <span style={{ color: '#276749' }}>Przetarg utworzony</span>;
  }
  if (item.status === 'skipped') {
    return <span style={{ color: '#718096' }}>Pominięto (duplikat)</span>;
  }
  if (item.status === 'failed' && item.errorMessage) {
    return (
      <span style={{ color: '#c53030' }} title={item.errorMessage}>
        {item.errorMessage.substring(0, 40)}
        {item.errorMessage.length > 40 ? '...' : ''}
      </span>
    );
  }
  if (item.status === 'pending' || item.status === 'processing') {
    return <span style={{ color: '#718096' }}>—</span>;
  }
  return <span style={{ color: '#718096' }}>—</span>;
}

function Timestamp({ value }: { value: string | null }) {
  const { relative, absolute } = formatTimestamp(value);
  return (
    <span className="timestamp" title={absolute}>
      {relative}
    </span>
  );
}

export function TenderLinksTable({ data, loading, onPageChange, onRowClick }: TenderLinksTableProps) {
  if (data.items.length === 0) {
    return (
      <div className="empty-state">
        <p>Brak wyników dla wybranych filtrów</p>
      </div>
    );
  }

  return (
    <div>
      <table className="admin-table">
        <thead>
          <tr>
            <th>Status</th>
            <th>Źródło</th>
            <th>Tytuł / URL</th>
            <th>Wynik</th>
            <th>Odkryto</th>
            <th>Przetworzone</th>
          </tr>
        </thead>
        <tbody>
          {data.items.map((item) => (
            <tr
              key={item.id}
              className="clickable"
              onClick={() => onRowClick(item)}
              style={{ opacity: loading ? 0.5 : 1 }}
            >
              <td>
                <StatusBadge status={item.status} />
              </td>
              <td>{item.sourceName}</td>
              <td style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {item.title || item.url}
              </td>
              <td>
                <ResultCell item={item} />
              </td>
              <td>
                <Timestamp value={item.discoveredAt} />
              </td>
              <td>
                <Timestamp value={item.processedAt} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Pagination pagination={data.pagination} onPageChange={onPageChange} />
    </div>
  );
}
